import React, { useState } from 'react';
import { Container } from '@/components/common/container';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KeenIcon } from '@/components/keenicons';
import { useRoles } from '@/hooks/admin/useRoles';
import { useDebounce } from '@/hooks/use-debounce';
import { RolesTable } from './components/RolesTable';
import { CreateRoleDialog } from './components/CreateRoleDialog';
import type { RoleQueryParams } from '@/services/admin/role.types';

/**
 * Roles List Page
 *
 * Route: /auth/roles
 * Access: Admins with Identity.Roles.Read permission
 *
 * Features:
 * - Browse and search roles
 * - Filter and sort roles
 * - Create new roles
 * - Navigate to role details
 * - Delete roles (with confirmation)
 *
 * Based on specs: docs/planning/admin-implementation/phase-1/03-AUTH-AUTHORIZATION-SPECS.md
 * Lines 20-83 (Roles List Page section)
 */
export function RolesListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    column: 'name' | 'permissionCount' | 'userCount';
    direction: 'asc' | 'desc';
  }>({
    column: 'name',
    direction: 'asc',
  });

  // Debounce search query to avoid excessive API calls
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Build query parameters for API
  const queryParams: RoleQueryParams = {
    filter: debouncedSearch || undefined,
    sorting: `${sortConfig.column} ${sortConfig.direction}`,
    skipCount: 0,
    maxResultCount: 50, // Show 50 roles per page initially
  };

  // Fetch roles with React Query
  const { data, isLoading, error, refetch } = useRoles(queryParams);

  const handleSortChange = (column: typeof sortConfig.column) => {
    setSortConfig((prev) => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleRoleCreated = () => {
    setIsCreateDialogOpen(false);
    refetch(); // Refresh the list
  };

  return (
    <Container>
      <div className="flex flex-col gap-6 py-6">
        {/* Page Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Role Management</h1>
            <p className="text-muted-foreground">
              Manage user roles and their permissions
            </p>
          </div>
          <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
            <KeenIcon icon="shield-tick" className="text-base" />
            Create Role
          </Button>
        </div>

        {/* Search and Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <KeenIcon
                  icon="magnifier"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  type="search"
                  placeholder="Search roles by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              {searchQuery && (
                <Button
                  variant="outline"
                  onClick={() => setSearchQuery('')}
                  className="gap-2"
                >
                  <KeenIcon icon="cross" className="text-base" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Roles Table */}
        <RolesTable
          roles={data?.items}
          totalCount={data?.totalCount}
          isLoading={isLoading}
          error={error}
          sortConfig={sortConfig}
          onSortChange={handleSortChange}
          onRoleDeleted={refetch}
        />

        {/* Results Summary */}
        {data && !isLoading && (
          <div className="text-sm text-muted-foreground">
            {data.totalCount === 0 ? (
              <p>No roles found</p>
            ) : (
              <p>
                Showing <span className="font-medium">{data.items.length}</span> of{' '}
                <span className="font-medium">{data.totalCount}</span> role
                {data.totalCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        )}

        {/* Create Role Dialog */}
        <CreateRoleDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onRoleCreated={handleRoleCreated}
        />
      </div>
    </Container>
  );
}
